import React, { useRef, ElementRef, useMemo, Fragment } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { ScheduleFFHelpers } from '@frontend/schedule-helpers';
import { usePreference } from '@frontend/user-preferences';
import { reorderColumnsUsingMap } from '../../hooks/utils/use-reordered-list';
import { CalendarColumnEntityListSection as ProviderListSection } from './calendar-column-entity-list-section';
import { CalendarScrollButtons } from './calendar-column-entity-list-section/scrollable-buttons';
import { CalendarEventCardV2 } from './calendar-event-card-v2';
import { CalendarEventHourLines } from './calendar-event-hour-lines';
import { CalendarViewContainer } from './calendar-view-container';
import { CalendarViewMainWrapper } from './calendar-view-main-wrapper';
import { PROVIDER_COLUMN_GAP, SCROLLABLE_BUTTON_WIDTH } from './constants';
import { EventTimelineWrapper } from './event-timeline-wrapper';
import { HoursOfTheDay } from './hours-of-the-day';
import { OverlappingEvents } from './overlapping-events';
import { TimeProgress } from './progress-lines';
import { useCalendarView, useCalendarViewProps, useColumnEntityReorderStore } from './providers';
import { EventData } from './types';
import { generateHourLabels, groupOverlappingEvents, moveUnassignedCalendarColumnEntity } from './utils';
import { checkIsHalfHourMarkersVisible, useZoomSafeContext } from './zoom/zoom-context';

// This is the width of the vertical hours lane where hours get displayed
const HOURS_LANE_WIDTH = 76;

const verticalLastEventGridSectionMarginRight = SCROLLABLE_BUTTON_WIDTH + PROVIDER_COLUMN_GAP;

export const CalendarViewMain = () => {
  const { pixelsPerMinute } = useZoomSafeContext();
  const { startHour, endHour, data, id, EmptyCalendarComponent, calendarDate } = useCalendarViewProps();
  const calendarViewWrapperRef = useRef<ElementRef<'article'>>(null);
  const providerListRef = useRef<ElementRef<'section'>>(null);
  const hourLabels = generateHourLabels(startHour, endHour, checkIsHalfHourMarkersVisible(pixelsPerMinute));
  const numberOfHours = hourLabels.length;
  const isTodaySelected = calendarDate === dayjs().format('MM/DD/YYYY');
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);
  const [columnEntityOrder] = usePreference('ScheduleCalendarViewColumnOrder');
  const { isEnabled: isColumnEntityReorderEnabled } = ScheduleFFHelpers.useCalendarColumnsReorderingFeatureFlag();

  const { hasProviders, gridTemplateColumns } = useMemo(() => {
    const hasProviders = data?.some((office) => !!office?.columnEntities?.length);
    // this is use to set the grid-template-columns for the vertical view for both top sticky provider list and the event grid
    const gridTemplateColumns = data
      ?.map((office) => (office?.columnEntities?.length ? `${office.columnEntities.length}fr` : ''))
      ?.join(' ');
    return { hasProviders, gridTemplateColumns };
  }, [data]);

  let isFirstColumnEntity = true;
  return (
    <>
      {!hasProviders && EmptyCalendarComponent && <EmptyCalendarComponent />}
      <CalendarViewMainWrapper containerRef={calendarViewWrapperRef} id={id}>
        <ProviderListSection
          ref={providerListRef}
          containerRef={calendarViewWrapperRef}
          gridTemplateColumns={gridTemplateColumns}
        />
        <CalendarViewContainer numberOfHours={numberOfHours}>
          <HoursOfTheDay hourLabels={hourLabels} pixelsPerMinute={pixelsPerMinute} />
          <article
            css={[
              css`
                grid-column: 1/-1;
                grid-row: 1 / -1;
                padding-left: ${HOURS_LANE_WIDTH}px;
                padding-right: ${verticalLastEventGridSectionMarginRight}px;
                display: ${isInReorderMode ? 'none' : 'grid'};
                grid-template-columns: ${gridTemplateColumns};
                grid-template-rows: auto;
                gap: ${PROVIDER_COLUMN_GAP}px;
              `,
            ]}
          >
            {data?.map((office, index) => {
              if (office.columnEntities?.length === 0) return null; // Do not show column if no providers

              // NOTE: This sorts the providers so that the unassigned provider is always at the end of the list
              const sortedColumnEntities = moveUnassignedCalendarColumnEntity(office?.columnEntities);

              const calendarEntityType = 'provider';
              const locationColumnOrderMap = columnEntityOrder?.[calendarEntityType]?.[office.locationId];
              const reorderedColumnEntities =
                locationColumnOrderMap && isColumnEntityReorderEnabled
                  ? reorderColumnsUsingMap(sortedColumnEntities, locationColumnOrderMap, 'entityId')
                  : sortedColumnEntities;

              return (
                <section
                  key={office.officeName + index}
                  css={{ height: '100%', display: 'flex', position: 'relative' }}
                >
                  {isTodaySelected && <TimeProgress numberOfHours={numberOfHours} pixelsPerMinute={pixelsPerMinute} />}
                  {reorderedColumnEntities.map((columnEntity) => {
                    const officeEvents = office?.officeBreaks || [];
                    const entityEvents = columnEntity.events.map((event) => ({
                      ...event,
                      entityId: columnEntity.entityId,
                      entityName: columnEntity.entityName,
                    }));
                    const allEvents = [...officeEvents, ...entityEvents];
                    const shouldRegisterResizeObserver = isFirstColumnEntity;
                    isFirstColumnEntity = false; // Set to false after the first iteration so that only the first column registers the resize observer

                    return (
                      <EventTimelineWrapper
                        key={columnEntity.entityId}
                        columnEntityId={columnEntity.entityId}
                        columnEntityName={columnEntity.entityName}
                        locationId={office.locationId}
                        calendarDateValue={office.calendarDateValue}
                        isRegisterResizeObserver={shouldRegisterResizeObserver}
                      >
                        <div css={{ position: 'relative' }}>
                          <EventCards
                            events={allEvents}
                            locationName={office.officeName}
                            calendarDate={office.calendarDateValue}
                          />
                        </div>
                      </EventTimelineWrapper>
                    );
                  })}
                </section>
              );
            })}
          </article>

          <CalendarEventHourLines
            hourLabels={hourLabels}
            shouldShowHoverIndicator={hasProviders}
            pixelsPerMinute={pixelsPerMinute}
          />
        </CalendarViewContainer>
      </CalendarViewMainWrapper>
      <CalendarScrollButtons containerRef={calendarViewWrapperRef} />
    </>
  );
};

interface EventCardsProps {
  events: EventData[];
  locationName: string;
  calendarDate?: string;
}

const EventCards: React.FC<EventCardsProps> = ({ events, locationName, calendarDate }) => {
  const { visibleLaneCount } = useCalendarView(['visibleLaneCount']);
  const eventsGroupedByOverlap = useMemo(() => groupOverlappingEvents(events), [events]);

  return eventsGroupedByOverlap.map((groupEvents, groupIndex) => {
    /*
      Override the visibleLaneCount to show all out of office and break events.
      This is to ensure that the out of office events are visible on calendar lane instead of carousel.
      It will be rare for a lane to have more than 2 out of office events at a given time. In normal case, it will be max 2, one for provider and one for location.
    */
    const outEventsCount = groupEvents.filter(
      (groupEvent) => groupEvent.eventType === 'outOfOffice' || groupEvent.eventType === 'break'
    ).length;
    const effectiveVisibleLaneCount = Math.max(visibleLaneCount, outEventsCount);

    return (
      <Fragment key={groupIndex}>
        {groupEvents.map((event, index) => (
          <CalendarEventCardV2
            key={`${event.eventId}-${index}`}
            id={event.eventId}
            title={event.name}
            startDate={event.startDate}
            calendarDate={calendarDate}
            startHour={event.startHour}
            endHour={event.endHour}
            type={event.eventType}
            laneCount={event.laneCount}
            laneIndex={event.laneIndex}
            providerId={event.providerId}
            providerName={event.providerName}
            locationName={locationName}
            locationId={event.locationId}
            visibleLaneCount={effectiveVisibleLaneCount}
            personStatus={event.personStatus}
          />
        ))}
        <OverlappingEvents overlappingEvents={groupEvents} visibleLaneCount={effectiveVisibleLaneCount} />
      </Fragment>
    );
  });
};
